import { API } from "aws-amplify";

/**
 * Validate promo code for deposit orders (in-memory validation)
 * @param {string} code - Promo code to validate
 * @param {Array} items - Cart items to validate against
 * @param {Object} show - Show information (optional)
 * @returns {Object} - Validation result with promo code data or error
 */
export async function validatePromoCodeForDeposit(code, items, show = null) {
  try {
    // Add show information to items if available
    const itemsWithShow = (Array.isArray(items) ? items : []).map(item => ({
      ...item,
      show_uid: show?._id || null
    }));

    const result = await API.post("vivelo", `/cart/promo-codes/validate`, {
      body: { code, items: itemsWithShow }
    });
    return result;
  } catch (error) {
    console.error('Error validating promo code:', error);
    throw error;
  }
}

/**
 * Calculate temporary discount for deposit orders
 * @param {Object} promoCode - Validated promo code object
 * @param {number} subtotal - Order subtotal
 * @returns {number} - Calculated discount amount
 */
export function calculateTemporaryDiscount(promoCode, subtotal) {
  if (!promoCode || !subtotal || subtotal <= 0) {
    return 0;
  }

  if (promoCode.discount_type === 'PERCENT') {
    const discountPercent = promoCode.discount_value / 100;
    return Math.round(subtotal * discountPercent);
  } else if (promoCode.discount_type === 'FIXED') {
    return Math.min(promoCode.discount_value, subtotal);
  }

  return 0;
}

/**
 * Calculate order totals with temporary promo code
 * @param {Array} items - Order items
 * @param {Object} promoCode - Validated promo code (optional)
 * @returns {Object} - Calculated totals
 */
export function calculateOrderTotalsWithPromo(items, promoCode = null) {
  // Calculate subtotal from items
  const safeItems = Array.isArray(items) ? items : [];
  const subtotal = safeItems.reduce((total, item) => {
    const itemPrice = item.price || 0;
    const itemQuantity = item.quantity || 1;
    const itemFee = item.fee || 0;
    return total + (itemPrice + itemFee) * itemQuantity;
  }, 0);

  // Calculate discount if promo code exists
  const discount = promoCode ? calculateTemporaryDiscount(promoCode, subtotal) : 0;
  
  // Calculate final total
  const total = subtotal - discount;

  return {
    subtotal,
    total_discount: discount,
    total,
    promo_codes: promoCode ? [{
      code: promoCode.code,
      promo_code_id: promoCode._id,
      discount_type: promoCode.discount_type,
      discount_value: promoCode.discount_value,
      applied_at: new Date(),
      discount_amount: discount
    }] : []
  };
}
